import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useSnackbar } from 'notistack';
import bancoService, {
  SucursalBancoImportFila,
  SucursalBancoImportResult,
} from '../../services/bancoService';
import { downloadBlob } from '../../utils/downloadBlob';

interface ImportarSucursalesBancoDialogProps {
  open: boolean;
  onClose: () => void;
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const extraerMensajeError = (error: any, porDefecto: string): string => {
  const data = error?.response?.data;
  if (!data) return porDefecto;
  if (typeof data === 'string') return data;
  if (data.message) return data.detail ? `${data.message} ${data.detail}` : data.message;
  return data.title || porDefecto;
};

const TablaMensajes: React.FC<{ titulo: string; items: SucursalBancoImportFila[]; color: 'error' | 'warning' }> = ({
  titulo,
  items,
  color,
}) => (
  <Box>
    <Typography variant="subtitle2" color={`${color}.main`} sx={{ mb: 0.5 }}>
      {titulo} ({items.length})
    </Typography>
    <TableContainer sx={{ maxHeight: 220, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 70 }}>Fila</TableCell>
            <TableCell sx={{ width: 140 }}>Columna</TableCell>
            <TableCell>Mensaje</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((e, i) => (
            <TableRow key={`${e.fila}-${e.columna}-${i}`}>
              <TableCell>{e.fila > 0 ? e.fila : 'General'}</TableCell>
              <TableCell>{e.columna || '-'}</TableCell>
              <TableCell>{e.mensaje}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const ImportarSucursalesBancoDialog: React.FC<ImportarSucursalesBancoDialogProps> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<SucursalBancoImportResult | null>(null);
  const [validando, setValidando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [descargando, setDescargando] = useState(false);

  useEffect(() => {
    if (open) {
      setArchivo(null);
      setResultado(null);
    }
  }, [open]);

  const ocupado = validando || importando;
  const hayParaGrabar = !!resultado && resultado.dryRun && (resultado.creadas > 0 || resultado.actualizadas > 0);

  const handleDescargarPlantilla = async () => {
    try {
      setDescargando(true);
      const blob = await bancoService.descargarPlantillaSucursales();
      downloadBlob(blob, 'Plantilla_Sucursales_Banco.xlsx', XLSX_MIME);
    } catch (error) {
      enqueueSnackbar(extraerMensajeError(error, 'No se pudo descargar la plantilla'), { variant: 'error' });
    } finally {
      setDescargando(false);
    }
  };

  const handleArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (file && !/\.(xlsx|xlsm|csv)$/i.test(file.name)) {
      enqueueSnackbar('Solo se admiten archivos .xlsx, .xlsm o .csv', { variant: 'warning' });
      return;
    }
    setArchivo(file);
    setResultado(null);
  };

  const ejecutar = async (dryRun: boolean) => {
    if (!archivo) {
      enqueueSnackbar('Seleccioná un archivo', { variant: 'warning' });
      return;
    }
    const setLoading = dryRun ? setValidando : setImportando;
    try {
      setLoading(true);
      const res = await bancoService.importarSucursales(archivo, dryRun);
      setResultado(res);
      if (!dryRun) {
        if (res.importado) {
          enqueueSnackbar(
            `Importación lista: ${res.creadas} sucursales nuevas y ${res.actualizadas} actualizadas.`,
            { variant: 'success' },
          );
        } else {
          enqueueSnackbar('No había sucursales para crear ni actualizar.', { variant: 'info' });
        }
      } else if (res.creadas === 0 && res.actualizadas === 0) {
        enqueueSnackbar('No hay sucursales nuevas ni datos para completar.', { variant: 'warning' });
      }
    } catch (error) {
      enqueueSnackbar(extraerMensajeError(error, dryRun ? 'Error al validar el archivo' : 'Error al importar'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={ocupado ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar sucursales desde Excel</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Cada fila es una sucursal de un banco que ya exista. Si no está cargada, se da de alta.
            Si ya existe, se completa el código de ciudad solo cuando está vacío o no figura en Ciudades,
            y el nombre solo si está en blanco. Una ciudad ya válida no se reemplaza.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Button
              variant="outlined"
              startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDescargarPlantilla}
              disabled={descargando || ocupado}
              sx={{ textTransform: 'none' }}
            >
              Descargar plantilla
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={ocupado}
              sx={{ textTransform: 'none' }}
            >
              Seleccionar archivo
              <input type="file" hidden accept=".xlsx,.xlsm,.csv" onChange={handleArchivo} />
            </Button>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {archivo ? archivo.name : 'Ningún archivo seleccionado'}
            </Typography>
          </Stack>

          {resultado && (
            <Stack spacing={2}>
              <Alert severity={resultado.importado ? 'success' : resultado.conError > 0 ? 'warning' : 'info'}>
                {resultado.dryRun
                  ? resultado.creadas + resultado.actualizadas > 0
                    ? 'Validación lista. Las filas con error no se graban. Si el resto está bien, presioná Importar.'
                    : 'No hay filas para crear ni para completar.'
                  : resultado.importado
                    ? 'Importación finalizada.'
                    : 'No se grabó ninguna sucursal.'}
              </Alert>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Filas leídas: ${resultado.filasLeidas}`} />
                <Chip size="small" color="success" variant="outlined" label={`Nuevas: ${resultado.creadas}`} />
                <Chip size="small" color="primary" variant="outlined" label={`Actualizadas: ${resultado.actualizadas}`} />
                <Chip size="small" variant="outlined" label={`Sin cambios: ${resultado.omitidas}`} />
                {resultado.conError > 0 && (
                  <Chip size="small" color="error" variant="outlined" label={`Con error: ${resultado.conError}`} />
                )}
              </Stack>

              {resultado.errores.length > 0 && (
                <TablaMensajes titulo="Errores" items={resultado.errores} color="error" />
              )}
              {resultado.erroresNoListados > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Hay {resultado.erroresNoListados} errores más que no se muestran en la lista.
                </Typography>
              )}
              {resultado.advertencias.length > 0 && (
                <TablaMensajes titulo="Advertencias" items={resultado.advertencias} color="warning" />
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={ocupado} sx={{ textTransform: 'none' }}>
          {resultado?.importado ? 'Cerrar' : 'Cancelar'}
        </Button>
        <Button
          variant="outlined"
          startIcon={validando ? <CircularProgress size={16} /> : <FactCheckIcon />}
          onClick={() => ejecutar(true)}
          disabled={!archivo || ocupado || !!resultado?.importado}
          sx={{ textTransform: 'none' }}
        >
          Validar
        </Button>
        <Button
          variant="contained"
          startIcon={importando ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          onClick={() => ejecutar(false)}
          disabled={!hayParaGrabar || ocupado}
          sx={{ textTransform: 'none' }}
        >
          Importar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportarSucursalesBancoDialog;
